import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { STRINGS } from "../lib/i18n";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const LS = {
  lang: "ha_lang",
  theme: "ha_theme",
  city: "ha_city",
  cart: "ha_cart",
  guest: "ha_guest_id",
  orders: "ha_orders",
};

function readJSON(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

function ensureGuestId() {
  let id = localStorage.getItem(LS.guest);
  if (!id) {
    id = "web_" + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());
    localStorage.setItem(LS.guest, id);
  }
  return id;
}

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(LS.lang) || "sv");
  const [theme, setTheme] = useState(() => localStorage.getItem(LS.theme) || "light");
  const [city, setCity] = useState(() => localStorage.getItem(LS.city) || "");
  const [cart, setCart] = useState(() => readJSON(LS.cart, []));
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState(() => readJSON(LS.orders, []));
  const [cartOpen, setCartOpen] = useState(false);
  const [guestId] = useState(ensureGuestId);

  const t = useMemo(() => STRINGS[lang], [lang]);
  const dir = t.dir;

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    localStorage.setItem(LS.lang, lang);
  }, [lang, dir]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem(LS.theme, theme);
  }, [theme]);

  useEffect(() => localStorage.setItem(LS.cart, JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem(LS.orders, JSON.stringify(orders)), [orders]);
  useEffect(() => localStorage.setItem(LS.city, city), [city]);

  const addToCart = useCallback((item) => {
    setCart((prev) => {
      const idx = prev.findIndex((p) => p.key === item.key && p.note === item.note);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + item.qty };
        return next;
      }
      return [...prev, item];
    });
  }, []);

  const updateCartItem = useCallback((index, patch) => {
    setCart((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }, []);

  const removeCartItem = useCallback((index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const addOrderRef = useCallback((ref) => {
    setOrders((prev) => [ref, ...prev.filter((o) => o.order_id !== ref.order_id)].slice(0, 20));
  }, []);

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

  const value = {
    lang, setLang, t, dir,
    theme, setTheme,
    city, setCity,
    cart, addToCart, updateCartItem, removeCartItem, clearCart, subtotal, cartCount,
    search, setSearch,
    cartOpen, setCartOpen,
    guestId,
    orders, addOrderRef,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
